import React, { useRef, useCallback } from 'react';
import { motion, type PanInfo } from 'framer-motion';

interface SubPanelSwiperProps {
  /** Index of the active sub-panel (0, 1, 2) */
  activePanel: number;
  /** Called when swipe gesture resolves to a new panel */
  onPanelChange: (index: number) => void;
  /** Current screen width in px (for snap math) */
  screenWidth: number;
  /** The 3 sub-panel content elements */
  children: [React.ReactNode, React.ReactNode, React.ReactNode];
}

const PANEL_COUNT = 3;
/** Gap between panels in px */
const PANEL_GAP = 12;

/** Minimum velocity (px/s) to trigger a panel change on a fast flick */
const VELOCITY_THRESHOLD = 300;
/** Minimum drag distance (fraction of screen width) for a slow drag to commit */
const DRAG_FRACTION = 0.25;
/**
 * Minimum movement (px) before we lock the gesture direction.
 * Prevents locking too early on noisy touch starts.
 */
const DIRECTION_LOCK_THRESHOLD = 5;

/**
 * SubPanelSwiper — horizontal drag container for the 3 sub-panels inside
 * one section.
 *
 * All 3 children are always mounted (state preserved). The container
 * translates via framer-motion `drag="x"` + spring snap.
 *
 * Smooth feel during drag (generous elastic), but ALWAYS snaps cleanly
 * to the nearest panel on release. Never stops between panels.
 *
 * Direction locking: if the first meaningful movement is more vertical
 * than horizontal, the gesture is treated as a scroll and the panel
 * always springs back to its current position on release.
 */
export function SubPanelSwiper({
  activePanel,
  onPanelChange,
  screenWidth,
  children,
}: SubPanelSwiperProps) {
  const panelWidth = screenWidth - PANEL_GAP;
  const totalWidth = PANEL_COUNT * panelWidth + (PANEL_COUNT - 1) * PANEL_GAP;
  const constraintLeft = -(totalWidth - screenWidth) + PANEL_GAP / 2;
  const isDragging = useRef(false);

  // Direction-lock state — reset each drag gesture
  const isVerticalDrag = useRef(false);
  const directionLocked = useRef(false);

  // Double-tap to recenter
  const lastTapRef = useRef(0);
  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // Double tap → go to center panel
      onPanelChange(1);
      // Reset any accidental zoom
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    }
    lastTapRef.current = now;
  }, [onPanelChange]);

  function handleDragStart() {
    isDragging.current = true;
    isVerticalDrag.current = false;
    directionLocked.current = false;
  }

  function handleDrag(_: unknown, info: PanInfo) {
    if (directionLocked.current) return;

    const dx = Math.abs(info.offset.x);
    const dy = Math.abs(info.offset.y);

    // Wait until we have enough movement to determine direction reliably
    if (dx < DIRECTION_LOCK_THRESHOLD && dy < DIRECTION_LOCK_THRESHOLD) return;

    directionLocked.current = true;
    // Vertical if Y movement exceeds X movement — treat as scroll, not swipe
    isVerticalDrag.current = dy > dx;
  }

  function handleDragEnd(_: unknown, info: PanInfo) {
    isDragging.current = false;

    // Gesture was primarily vertical (scroll intent) — always snap back to current panel
    if (isVerticalDrag.current) {
      onPanelChange(activePanel);
      return;
    }

    const { offset, velocity } = info;

    let next = activePanel;

    // Fast flick → go to next/prev panel regardless of distance
    if (Math.abs(velocity.x) > VELOCITY_THRESHOLD) {
      next = velocity.x < 0
        ? Math.min(activePanel + 1, PANEL_COUNT - 1)
        : Math.max(activePanel - 1, 0);
    }
    // Slow drag → commit if past threshold fraction of screen width
    else if (Math.abs(offset.x) > screenWidth * DRAG_FRACTION) {
      next = offset.x < 0
        ? Math.min(activePanel + 1, PANEL_COUNT - 1)
        : Math.max(activePanel - 1, 0);
    }
    // Otherwise spring back to current panel (no change)

    onPanelChange(next);
  }

  // Calculate snap position for each panel (offset by half gap to center in viewport)
  const getSnapX = (panel: number) => -(panel * (panelWidth + PANEL_GAP)) + PANEL_GAP / 2;

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{
        touchAction: 'pan-y',
        overscrollBehavior: 'contain',
        maxWidth: '100vw',
        minWidth: '100vw',
      }}
      onTouchEnd={handleDoubleTap}
    >
      <motion.div
        drag="x"
        dragConstraints={{ left: constraintLeft, right: PANEL_GAP / 2 }}
        // Generous elastic during drag = smooth feel, but snap animation
        // always brings it cleanly to panel center on release
        dragElastic={0.15}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        animate={{ x: getSnapX(activePanel) }}
        // Tight snap-back spring: fast, no overshoot
        transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300,
          mass: 0.8,
        }}
        style={{
          display: 'flex',
          gap: PANEL_GAP,
          width: totalWidth,
          height: '100%',
          willChange: 'transform',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {children.map((child, i) => (
          <div
            key={i}
            style={{
              width: panelWidth,
              minWidth: panelWidth,
              height: '100%',
              overflow: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              flexShrink: 0,
            }}
          >
            {child}
          </div>
        ))}
      </motion.div>
    </div>
  );
}
